// FILE: useReconcileComposerModelSelection.ts
// Purpose: Keep the composer's model selection inside what the user is allowed
//          to dispatch, by seeding the draft when policy disagrees with the
//          inherited default.
// Layer: Web hooks
// Exports: resolvePolicyModelSelectionCorrection, useReconcileComposerModelSelection.

import { useEffect, useRef } from "react";

import type { ModelSelection, ProviderKind, ThreadId } from "@taskin-code/contracts";

import type { ModelSelectionPolicy } from "~/allowedModelSelection";
import {
  type ComposerThreadDraftState,
  resolvePreferredComposerModelSelection,
  useComposerDraftStore,
  useComposerThreadDraft,
} from "~/composerDraftStore";
import { useDefaultModel } from "~/auth/authStore";
import { useModelSelectionPolicy } from "~/hooks/useModelSelectionPolicy";

export interface ComposerModelSelectionChain {
  readonly draft:
    | Pick<ComposerThreadDraftState, "modelSelectionByProvider" | "activeProvider">
    | null
    | undefined;
  readonly threadModelSelection: ModelSelection | null | undefined;
  readonly projectModelSelection: ModelSelection | null | undefined;
  readonly userDefaultModelSelection: ModelSelection | null | undefined;
  readonly defaultProvider: ProviderKind;
}

/**
 * The selection to write, or `null` when policy has nothing to correct.
 *
 * Comparing the policy-aware answer against the policy-blind one — rather than
 * against whatever the composer currently shows — is what keeps this quiet for
 * accounts with a full allowlist: same answer, nothing to write, no render loop.
 * It also makes the correction idempotent, since a seeded draft feeds back in as
 * the top of the chain and both answers then agree.
 */
export function resolvePolicyModelSelectionCorrection(
  chain: ComposerModelSelectionChain,
  policy: ModelSelectionPolicy | null,
): ModelSelection | null {
  if (policy === null) return null;

  const inherited = resolvePreferredComposerModelSelection(chain);
  const allowed = resolvePreferredComposerModelSelection({ ...chain, policy });

  const sameSelection =
    allowed.provider === inherited.provider &&
    allowed.model === inherited.model &&
    (allowed.agentId ?? null) === (inherited.agentId ?? null);

  return sameSelection ? null : allowed;
}

/**
 * Write the policy-corrected selection into the thread's draft when the
 * inherited one is not usable.
 *
 * The creation paths reconcile at the point they persist a thread, but the
 * composer derives what it *shows* from a separate per-provider chain
 * (`deriveEffectiveComposerModelState`) that starts from the project default and
 * the app's default provider. For an account granted one agent and no models,
 * that chain lands on a model the server refuses, so the user sees a foreign
 * model slug and the "Modelo no autorizado" badge before they have done
 * anything wrong.
 *
 * Seeding the draft — rather than overriding the derivation — is what makes the
 * correction total: the draft is authoritative for the provider *and* the model,
 * so `selectedProvider`, the picker's label, the dispatched selection and the
 * badge all follow from one write, instead of each needing its own special case.
 * It is exactly the state the user would have produced by opening the picker and
 * choosing the agent themselves.
 *
 * Only ever runs for a thread that has not started: once a provider session
 * exists it cannot be switched without restarting it, so a correction there
 * would be a promise the dispatch path silently drops.
 */
export function useReconcileComposerModelSelection(input: {
  readonly threadId: ThreadId;
  readonly hasThreadStarted: boolean;
  readonly threadModelSelection: ModelSelection | null | undefined;
  readonly projectModelSelection: ModelSelection | null | undefined;
  readonly defaultProvider: ProviderKind;
}): void {
  const draft = useComposerThreadDraft(input.threadId);
  const userDefaultModel = useDefaultModel();
  const policy = useModelSelectionPolicy();
  const setModelSelection = useComposerDraftStore((store) => store.setModelSelection);
  // Guards against a fight with a user who deliberately picks something the
  // policy would overwrite: one correction per thread, not a standing override.
  const seededThreadIdRef = useRef<ThreadId | null>(null);

  useEffect(() => {
    if (input.hasThreadStarted) return;
    if (seededThreadIdRef.current === input.threadId) return;

    const correction = resolvePolicyModelSelectionCorrection(
      {
        draft,
        threadModelSelection: input.threadModelSelection,
        projectModelSelection: input.projectModelSelection,
        userDefaultModelSelection: userDefaultModel,
        defaultProvider: input.defaultProvider,
      },
      policy,
    );
    if (correction === null) return;

    seededThreadIdRef.current = input.threadId;
    setModelSelection(input.threadId, correction);
  }, [
    draft,
    input.defaultProvider,
    input.hasThreadStarted,
    input.projectModelSelection,
    input.threadId,
    input.threadModelSelection,
    policy,
    setModelSelection,
    userDefaultModel,
  ]);
}
